import type { AdminPermission } from '@carcheck/shared';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useHasPermission } from '../../auth/usePermissions';

/**
 * PII display gate (PLAN §3.7 / §1.4).
 *
 * Shows `value` (full) only when the admin holds `perm`; otherwise shows
 * `redacted` (the server-side pre-redacted variant). **Fail-closed**:
 * if the permission is absent / unknown, the redacted form renders — the
 * full value never reaches the DOM via this component when ungated.
 *
 * `copyable` adds a copy-to-clipboard button — only rendered when the
 * admin is permitted to see the full value (copying a redacted string is
 * useless and copying full PII without permission would be a leak).
 *
 * Wrapped in `observer` so a permission change re-evaluates reactively.
 */
export interface RedactedFieldProps {
  /** Permission that unlocks the full value. */
  perm: AdminPermission;
  /** Full, unredacted value (only shown when permitted). */
  value: string;
  /** Pre-redacted server-side variant shown when permission is absent. */
  redacted: string;
  /** Adds a copy button (permitted admins only). */
  copyable?: boolean;
}

const RedactedFieldInner = ({
  perm,
  value,
  redacted,
  copyable = false,
}: RedactedFieldProps): ReactElement => {
  const { t } = useTranslation();
  const allowed = useHasPermission(perm);
  const [copied, setCopied] = useState(false);

  const display = allowed ? value : redacted;

  const handleCopy = (): void => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography component="span" variant="body2">
        {display}
      </Typography>
      {!allowed ? (
        <Tooltip title={t('pii.redactedHint')}>
          <VisibilityIcon
            fontSize="inherit"
            sx={{ color: 'text.disabled' }}
            aria-label={t('pii.redactedHint')}
          />
        </Tooltip>
      ) : null}
      {allowed && copyable ? (
        <Tooltip title={copied ? t('pii.copied') : t('pii.copy')}>
          <IconButton size="small" onClick={handleCopy} aria-label={t('pii.copy')} sx={{ p: 0.25 }}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export const RedactedField = observer(RedactedFieldInner);
